import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldCheck, Sparkles, ArrowRight, Mic, Camera, BookmarkCheck, History,
  AlertTriangle, Clock, Copy, Stethoscope, FileStack, ClipboardCheck, Building2,
  Loader2, FileWarning, RotateCw, Zap,
} from "lucide-react";
import AvidaLogo from "@/components/brand/AvidaLogo";
import CopyFromLastModal from "@/components/CopyFromLastModal";
import OrganizationPicker, { useSelectedOrg } from "@/components/OrganizationPicker";
import { allDhsIssues } from "@/lib/dhsRules";
import { fieldToStep } from "@/lib/fieldToStep";
import { useWizard } from "@/context/WizardContext";
import { useIntegrationMode, useAssignmentContext } from "@/lib/integrationMode";
import {
  flushStandaloneQueue, getStandaloneQueueCount,
} from "@/lib/avidaDraft";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// No hero photo — we show the Avida logo in the hero instead to push brand.
const STORAGE_KEY = "wi-pa-wizard-v1";

function estimateMinutes(errors, warnings) {
  return Math.max(1, Math.ceil((errors * 45 + warnings * 20) / 60));
}

function computeReadiness(d) {
  const issues = allDhsIssues(d);
  const byStep = {};
  for (const i of issues) {
    const t = fieldToStep(i.field);
    if (!t) continue;
    const b = byStep[t.step] || (byStep[t.step] = { error: 0, warning: 0 });
    if (i.severity === "error") b.error += 1;
    else if (i.severity === "warning") b.warning += 1;
  }
  const errorCount = issues.filter((i) => i.severity === "error").length;
  const warningCount = issues.filter((i) => i.severity === "warning").length;
  const compliantSteps = Array.from({ length: 10 }, (_, n) => n + 1).filter(
    (step) => !(byStep[step] && byStep[step].error > 0)
  ).length;
  const firstBadStep = [...Object.entries(byStep)]
    .filter(([, b]) => b.error > 0 || b.warning > 0)
    .sort((a, b) => (b[1].error - a[1].error) || (b[1].warning - a[1].warning))
    .map(([s]) => Number(s))[0];
  return { errorCount, warningCount, compliantSteps, estMinutes: estimateMinutes(errorCount, warningCount), firstBadStep };
}

function hasDraft() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    const c = d?.client || {};
    const signs = d?.signatures || {};
    const name = `${c.first_name || ""} ${c.last_name || ""}`.trim();
    // A real draft requires client demographics or clinical content.
    // Agency name alone doesn't count — it's auto-populated by the Organization Picker
    // before any actual intake has happened.
    const hasContent =
      (name && name.length > 0) ||
      (c.member_id || "").length > 0 ||
      (Array.isArray(d?.diagnoses) && d.diagnoses.some((x) => (x.icd10 || x.description || "").trim())) ||
      (Array.isArray(d?.medications) && d.medications.some((x) => (x.name || "").trim()));
    if (!hasContent) return null;
    return {
      name: name || "(unnamed draft)",
      agency: d?.agency?.name || "",
      memberId: c.member_id || "",
      dxCount: (d?.diagnoses || []).filter((x) => (x.icd10 || x.description || "").trim()).length,
      rxCount: (d?.medications || []).filter((x) => (x.name || "").trim()).length,
      signed: !!(signs.rn_name || "").trim(),
      readiness: computeReadiness(d),
    };
  } catch {
    return null;
  }
}

export default function Landing() {
  const [draft, setDraft] = useState(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [orgOpen, setOrgOpen] = useState(false);
  const [visitLoading, setVisitLoading] = useState(false);
  const [queuedCount, setQueuedCount] = useState(0);
  const [flushing, setFlushing] = useState(false);
  const selectedOrg = useSelectedOrg();
  const integrationMode = useIntegrationMode();
  const assignmentCtx = useAssignmentContext();
  const navigate = useNavigate();
  const { setData } = useWizard();

  // Phase 6J — flush any standalone intakes queued offline. Tries on
  // mount + on `online` events. Renders a yellow "N pending sync" badge
  // until the queue is empty.
  useEffect(() => {
    const refresh = () => setQueuedCount(getStandaloneQueueCount());
    refresh();
    const tryFlush = async () => {
      if (getStandaloneQueueCount() === 0) return;
      setFlushing(true);
      try {
        const r = await flushStandaloneQueue();
        if (r.flushed > 0) {
          toast.success(`${r.flushed} standalone intake${r.flushed === 1 ? "" : "s"} synced`);
        }
        setQueuedCount(r.remaining);
      } finally {
        setFlushing(false);
      }
    };
    tryFlush();
    window.addEventListener("online", tryFlush);
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener("online", tryFlush);
      window.removeEventListener("focus", refresh);
    };
  }, []);

  const startStandaloneIntake = () => {
    try { sessionStorage.setItem("wi-pa-standalone-mode-v1", "1"); }
    catch { /* non-fatal */ }
    // Make sure no Avida deep-link state leaks into the standalone session.
    try { sessionStorage.removeItem("wi-pa-avida-draft-id-v1"); }
    catch { /* non-fatal */ }
    navigate("/wizard/1?standalone=1");
  };

  const manualFlush = async () => {
    setFlushing(true);
    try {
      const r = await flushStandaloneQueue();
      if (r.flushed > 0) toast.success(`${r.flushed} synced`);
      else if (r.remaining > 0) toast.error(`Sync failed — ${r.remaining} still pending`);
      setQueuedCount(r.remaining);
    } finally {
      setFlushing(false);
    }
  };

  useEffect(() => { setDraft(hasDraft()); }, []);

  // Stage 1 deep-link handler — when the parent app sends the nurse to
  // `/?visit_id=xxx`, fetch the prepopulated visit context and seed the
  // wizard with it before routing to Step 1. Stores the visit id in
  // localStorage so the Step 9 handoff can reference it on submission.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const visitId = params.get("visit_id");
    if (!visitId) return;

    setVisitLoading(true);
    // Resume-from-checkpoint: if the parent app deep-links with a visit_id
    // that already has a server-side mid-flight draft (RN switched devices,
    // tablet crashed), restore from the checkpoint INSTEAD OF re-seeding
    // from the bare visit context. That way the RN walks back into work-
    // in-progress, not a fresh wizard.
    Promise.all([
      axios.get(`${API}/visits/intake/${visitId}`),
      axios.get(`${API}/visits/${visitId}/draft`).catch((e) => {
        if (e.response?.status === 404) return null;  // no checkpoint yet — fine
        throw e;
      }),
    ])
      .then(([ctxRes, draftRes]) => {
        const state = ctxRes.data?.state || {};
        const recertSeed = state._recert_seed;
        const isRecert = !!state._visit?.is_recert;
        const wizardState = { ...state };
        delete wizardState._recert_seed;
        const checkpoint = draftRes?.data;
        const resuming = !!(checkpoint && checkpoint.state && Object.keys(checkpoint.state).length > 0);

        // Precedence (lowest → highest):
        //   1. Wizard's default INITIAL_STATE (in `d` from setData callback)
        //   2. Recert snapshot from parent (last visit's wizard data — recert seed)
        //   3. Intake context (fresh demographics + records the parent just pushed)
        //   4. Server-side mid-flight checkpoint (RN was already in progress)
        //
        // Single setData with deterministic ordering. The snapshot fills
        // EVERY field — including ones with placeholder defaults like
        // diagnoses: [{icd10:'', description:''}] — so snapshot diagnoses
        // survive when intake didn't supply them. Intake overrides snapshot
        // for the bits parent re-pushed (demographics, physician). Checkpoint
        // wins for everything if the RN was already mid-visit.
        const snapshot = (recertSeed?.snapshot && !resuming) ? recertSeed.snapshot : {};
        const checkpointState = resuming ? checkpoint.state : {};
        setData((d) => ({ ...d, ...snapshot, ...wizardState, ...checkpointState }));

        if (recertSeed?.snapshot && !resuming) {
          // Also seed the recert-diff baseline so the existing diff card works.
          try {
            localStorage.setItem(
              "wi-pa-copy-source-v1",
              JSON.stringify({
                job_id: recertSeed.meta?.last_job_id || `recert-${visitId}`,
                client_name: `${state.client?.first_name || ""} ${state.client?.last_name || ""}`.trim() || "Recert client",
                copied_at: Date.now(),
                snapshot: recertSeed.snapshot,
                source: "parent_app_recert",
                last_visit_date: recertSeed.meta?.last_visit_date || "",
              }),
            );
          } catch { /* localStorage unavailable — non-fatal */ }
        }
        try {
          localStorage.setItem("wi-pa-active-visit-v1", JSON.stringify({
            visit_id: ctxRes.data.visit_id,
            external_visit_id: state?._visit?.external_visit_id || "",
            loaded_at: new Date().toISOString(),
          }));
        } catch { /* non-fatal */ }

        const lastSavedAt = checkpoint?.updated_at ? new Date(checkpoint.updated_at).toLocaleString() : null;
        toast.success(
          resuming
            ? `Resumed mid-flight visit (last saved ${lastSavedAt}). Pick up where you left off.`
            : isRecert
              ? "Re-cert visit loaded — wizard pre-populated from your last assessment. Review each step carefully."
              : "Visit context loaded — wizard pre-populated.",
          { duration: resuming || isRecert ? 6000 : 3000 },
        );
        // Clean the URL so a refresh doesn't re-fetch.
        window.history.replaceState({}, "", "/");
        navigate(`/wizard/${resuming ? checkpoint.current_step || 1 : 1}`);
      })
      .catch((e) => {
        const detail = e.response?.data?.detail || e.message;
        toast.error(`Could not load visit context: ${detail}`);
      })
      .finally(() => setVisitLoading(false));
    // We intentionally only run this on mount; setData and navigate are stable
    // across renders (from React-Router/Context).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const clearDraft = () => {
    // clearDraft is the user's explicit "wipe everything" action — if it
    // silently fails the user thinks the draft cleared but it persists.
    // Surface a toast in that rare case so they know to try again.
    let removeOk = true;
    try { localStorage.removeItem(STORAGE_KEY); } catch { removeOk = false; }
    try { localStorage.removeItem("wi-pa-copy-source-v1"); } catch { removeOk = false; }
    if (!removeOk) {
      // Most commonly happens in private-mode browsers where storage is read-only.
      // Surface to the user; in-memory state still resets.
      toast.error("Could not clear local draft (storage may be read-only). Try a different browser or close private mode.");
    }
    setDraft(null);
  };
  const afterCopy = () => setDraft(hasDraft());

  return (
    <main className="min-h-screen bg-hero" data-testid="landing-page">
      {visitLoading && (
        <div
          className="fixed inset-0 z-[200] bg-[#0B1E3A]/60 backdrop-blur-sm flex items-center justify-center"
          data-testid="visit-loading-overlay"
        >
          <div className="bg-white rounded-2xl px-7 py-6 shadow-2xl border border-[#D6E3EF] flex items-center gap-4">
            <Loader2 className="w-5 h-5 animate-spin text-[#0A3D79]" />
            <div>
              <div className="font-display text-[15px] font-semibold text-[#0B1E3A]">
                Loading visit context…
              </div>
              <div className="text-[12.5px] text-[#3A5275] mt-0.5">
                Pre-populating wizard from parent app.
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-8 lg:py-10">
        {/* =========================  HEADER ========================= */}
        <header className="flex items-center justify-between mb-14">
          <Link to="/" className="flex items-center gap-4" data-testid="landing-logo">
            <AvidaLogo size="md" iconOnly className="rounded-xl" />
            <div className="hidden md:block pl-4 border-l border-[#D6E3EF]">
              <div className="font-display text-[15.5px] font-semibold text-[#0A3D79] leading-tight tracking-tight">
                Avida Health
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A6B00] mt-1 font-bold">
                NSA Workflow
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/submissions" className="btn-ghost hidden sm:inline-flex" data-testid="landing-submissions">
              Submissions
            </Link>
            <button
              type="button"
              onClick={() => setOrgOpen(true)}
              className="btn-accent"
              data-testid="landing-cta"
            >
              Start intake <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </header>

        {/* =========================  RESUME CARD ========================= */}
        {draft && (
          <div className="card-premium mb-12 fade-up" data-testid="landing-resume-card">
            <div className="flex flex-wrap md:flex-nowrap items-center gap-5">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 bg-[#FFDA0B]">
                <History className="w-6 h-6 text-[#072A54]" strokeWidth={2.2} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="eyebrow mb-1">Resume where you left off</div>
                <div className="font-display text-xl lg:text-2xl font-semibold text-[#0B1E3A] mt-1 truncate tracking-tight">
                  {draft.name}{draft.agency ? <span className="text-[#3A5275]"> · {draft.agency}</span> : ""}
                </div>
                <div className="text-[13px] text-[#3A5275] mt-1.5 flex flex-wrap gap-x-5 gap-y-1">
                  {draft.memberId && <span>Member <span className="text-[#0B1E3A] font-semibold">{draft.memberId}</span></span>}
                  <span><span className="text-[#0B1E3A] font-semibold">{draft.dxCount}</span> diagnos{draft.dxCount === 1 ? "is" : "es"}</span>
                  <span><span className="text-[#0B1E3A] font-semibold">{draft.rxCount}</span> medication{draft.rxCount === 1 ? "" : "s"}</span>
                  {draft.signed && <span className="text-[#0A3D79] font-semibold">✓ RN signed</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={clearDraft} className="btn-ghost" data-testid="landing-discard-draft">Discard</button>
                <Link to={`/wizard/${draft.readiness.firstBadStep || 1}`} className="btn-accent" data-testid="landing-resume-btn">
                  Resume <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Compliance readiness strip */}
            <div className="mt-6 pt-5 border-t border-[#E7EEF3] grid md:grid-cols-[1fr_auto] gap-5 items-center" data-testid="landing-readiness">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className={`w-4 h-4 ${draft.readiness.errorCount === 0 ? "text-[#0A3D79]" : "text-[#c05746]"}`} />
                    <span className="eyebrow-blue text-[11px]">Compliance readiness</span>
                  </div>
                  <span className="text-[13px] font-semibold text-[#0B1E3A]" data-testid="landing-readiness-ratio">
                    <span className="text-[#0A3D79] font-bold">{draft.readiness.compliantSteps}</span><span className="text-[#3A5275]"> of 10 steps compliant</span>
                  </span>
                </div>
                <div className="h-2 rounded-full bg-[#E7EEF3] overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{
                      width: `${(draft.readiness.compliantSteps / 10) * 100}%`,
                      backgroundColor:
                        draft.readiness.errorCount === 0
                          ? "var(--avida-navy)"
                          : draft.readiness.errorCount <= 2
                          ? "var(--avida-yellow-dark)"
                          : "#c05746",
                    }}
                  />
                </div>
                <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-[12.5px]">
                  {draft.readiness.errorCount > 0 && (
                    <span className="inline-flex items-center gap-1 text-[#c05746] font-semibold" data-testid="landing-readiness-errors">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      {draft.readiness.errorCount} compliance error{draft.readiness.errorCount > 1 ? "s" : ""}
                    </span>
                  )}
                  {draft.readiness.warningCount > 0 && (
                    <span className="inline-flex items-center gap-1 text-[#8A6B00] font-semibold">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      {draft.readiness.warningCount} warning{draft.readiness.warningCount > 1 ? "s" : ""}
                    </span>
                  )}
                  {draft.readiness.errorCount === 0 && draft.readiness.warningCount === 0 && (
                    <span className="inline-flex items-center gap-1 text-[#0A3D79] font-semibold" data-testid="landing-readiness-clean">
                      <ShieldCheck className="w-3.5 h-3.5" />
                      Compliance checks satisfied — ready to sign &amp; submit.
                    </span>
                  )}
                  {(draft.readiness.errorCount > 0 || draft.readiness.warningCount > 0) && (
                    <span className="inline-flex items-center gap-1 text-[#3A5275]">
                      <Clock className="w-3.5 h-3.5" /> ~{draft.readiness.estMinutes} min to fix
                    </span>
                  )}
                </div>
              </div>
              {draft.readiness.firstBadStep && (
                <Link
                  to={`/wizard/${draft.readiness.firstBadStep}`}
                  className="btn-ghost whitespace-nowrap justify-self-start md:justify-self-end"
                  data-testid="landing-readiness-jump"
                >
                  Fix step {draft.readiness.firstBadStep} first <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          </div>
        )}

        {/* =========================  HERO ========================= */}
        <section className="grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-3 space-y-7">
            <div className="eyebrow">
              Clinical intake · Built for the home visit
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-[66px] leading-[1.04] tracking-tight text-[#0B1E3A]">
              Every client visit,<br />
              documented with the{" "}
              <span className="yellow-underline">care</span> they deserve.
            </h1>
            <p className="text-[17px] text-[#3A5275] max-w-xl leading-relaxed">
              The RN assessment is the source of truth. Avida Health turns your bedside observations
              into a compliant, pixel-perfect PA packet — so the physician can certify, the payer
              can authorize, and the care plan arrives in the client&apos;s home on time.
            </p>
            <div className="flex flex-wrap items-center gap-3 pt-2">
              {(() => {
                const isProduction = integrationMode?.mode === "production";
                const hasAssignment = assignmentCtx.source !== "none";

                if (hasAssignment) {
                  // Already assigned (avida / parent / preview pick) →
                  // straight to Step 1.
                  return (
                    <Link
                      to="/wizard/1"
                      className="btn-accent"
                      data-testid="landing-start-btn"
                    >
                      {assignmentCtx.source === "preview" ? "Open intake" : "Continue intake"}
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  );
                }

                if (isProduction) {
                  // Hard block — no manual agency picking in production.
                  return (
                    <button
                      type="button"
                      disabled
                      className="btn-accent opacity-50 cursor-not-allowed"
                      data-testid="landing-start-blocked"
                      title="An assignment is required to start an intake"
                    >
                      Awaiting assignment <ArrowRight className="w-4 h-4" />
                    </button>
                  );
                }

                // Preview mode (no parent/Avida secrets) — escape hatch.
                return (
                  <button
                    type="button"
                    onClick={() => setOrgOpen(true)}
                    className="btn-accent"
                    data-testid="landing-start-btn"
                  >
                    Preview · pick an organization <ArrowRight className="w-4 h-4" />
                  </button>
                );
              })()}
              <button
                type="button"
                onClick={() => setCopyOpen(true)}
                className="btn-ghost"
                data-testid="landing-copy-from-btn"
                title="Reuse a previous submission's demographics, diagnoses, meds, and care plan"
              >
                <Copy className="w-4 h-4" /> Copy from last visit
              </button>
              <button
                type="button"
                onClick={startStandaloneIntake}
                className="btn-ghost border-[#F0E29A] hover:bg-[#FFFDEC] text-[#8A6B00]"
                data-testid="landing-standalone-btn"
                title="No assignment / patient not in system / network glitch — capture the visit anyway"
              >
                <FileWarning className="w-4 h-4" /> Start standalone intake
              </button>
            </div>

            {queuedCount > 0 && (
              <div
                className="mt-4 max-w-xl rounded-xl border border-[#F0E29A] bg-[#FFFDEC] p-3 text-[13px] text-[#8A6B00] flex items-center gap-3"
                data-testid="landing-standalone-queue-badge"
              >
                <Clock className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1">
                  <span className="font-semibold text-[#0B1E3A]">
                    {queuedCount} standalone intake{queuedCount === 1 ? "" : "s"} pending sync
                  </span>{" "}
                  · captured offline, will upload when you have connectivity.
                </span>
                <button
                  type="button"
                  onClick={manualFlush}
                  disabled={flushing}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-[#0A3D79] text-[#0A3D79] bg-white hover:bg-[#EEF5FC] disabled:opacity-60 text-[11px] font-semibold"
                  data-testid="landing-standalone-flush-btn"
                >
                  {flushing ? <><Loader2 className="w-3 h-3 animate-spin" /> Syncing…</>
                            : <><RotateCw className="w-3 h-3" /> Sync now</>}
                </button>
              </div>
            )}

            {integrationMode?.mock_enabled && (
              <div
                className="mt-4 max-w-xl rounded-xl border-2 border-dashed border-[#9333EA] bg-[#FAF5FF] p-4 text-[13px]"
                data-testid="landing-avida-mock-banner"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#9333EA] text-white text-[10px] font-bold">
                    DEV
                  </span>
                  <span className="font-semibold text-[#581C87]">
                    Avida Care loopback simulator active
                  </span>
                </div>
                <p className="text-[#6B21A8] mb-3 leading-relaxed">
                  ENABLE_AVIDA_MOCK is on. The button below pretends to be Avida
                  Care sending a coordinator-built seed-draft, then opens the
                  resulting wizard URL — full integration end-to-end without
                  touching the real Avida project.
                </p>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/_dev/avida-mock/seed`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({}),
                      });
                      const j = await r.json();
                      if (j.ok && j.response?.wizard_url) {
                        toast.success(
                          `Seeded draft ${j.response.draft_id?.slice(0, 8) || ""} as ${j.request.assigned_rn_email}`,
                        );
                        // Brief delay so the toast renders before navigation
                        setTimeout(() => {
                          window.location.href = j.response.wizard_url;
                        }, 800);
                      } else {
                        toast.error(`Mock seed failed: HTTP ${j.status_code}`);
                      }
                    } catch (e) {
                      toast.error(`Mock seed error: ${e.message}`);
                    }
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#9333EA] text-white text-[13px] font-semibold hover:bg-[#7E22CE] transition"
                  data-testid="landing-avida-mock-seed-btn"
                >
                  <Zap className="w-4 h-4" /> Simulate Avida seed → open wizard
                </button>
              </div>
            )}

            {integrationMode?.mode === "production" && assignmentCtx.source === "none" && (
              <div
                className="mt-4 max-w-xl rounded-xl border border-[#F0E29A] bg-[#FFFDEC] p-4 text-[13px] text-[#8A6B00]"
                data-testid="landing-no-assignment-block"
              >
                <div className="font-semibold mb-1 text-[#0B1E3A]">
                  Awaiting assignment from your agency or Avida Care
                </div>
                <p>
                  This intake portal does not let nurses pick clients or agencies
                  directly. Open this app from the assignment link your agency
                  coordinator (or Avida Care) sent you, and the visit will load
                  here automatically.
                </p>
              </div>
            )}

            {assignmentCtx.source !== "none" && (
              <div
                className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#D6E3EF] bg-white px-3 py-1.5 text-[12.5px] text-[#3A5275]"
                data-testid="landing-assignment-source"
              >
                <Building2 className="w-3.5 h-3.5 text-[#0A3D79]" />
                Working on behalf of:{" "}
                <span className="text-[#0B1E3A] font-semibold">{assignmentCtx.label}</span>
                {assignmentCtx.source === "preview" && (
                  <button
                    type="button"
                    onClick={() => setOrgOpen(true)}
                    className="ml-2 text-[#8A6B00] hover:text-[#0A3D79] font-semibold"
                    data-testid="landing-change-preview-org"
                  >
                    Change
                  </button>
                )}
              </div>
            )}

            {selectedOrg && assignmentCtx.source === "none" && (
              <div
                className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#D6E3EF] bg-white px-3 py-1.5 text-[12.5px] text-[#3A5275]"
                data-testid="landing-selected-org"
              >
                <Building2 className="w-3.5 h-3.5 text-[#0A3D79]" />
                Organization: <span className="text-[#0B1E3A] font-semibold">{selectedOrg.name}</span>
                <button
                  type="button"
                  onClick={() => setOrgOpen(true)}
                  className="ml-2 text-[#8A6B00] hover:text-[#0A3D79] font-semibold"
                >
                  Change
                </button>
              </div>
            )}

            {/* Three pillars strip */}
            <div className="pt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 stagger-in">
              {[
                {
                  icon: Stethoscope,
                  label: "Client care",
                  body: "93-question NSA, MAHC-10, Braden, Morse — rich enough to build a real care plan.",
                },
                {
                  icon: ShieldCheck,
                  label: "Compliance",
                  body: "Live rules engine on every keystroke. Dual signatures + 13-item verification gate.",
                },
                {
                  icon: ClipboardCheck,
                  label: "Accuracy",
                  body: "Pixel-perfect overlays on the official state templates. Zero retyping.",
                },
              ].map((p) => (
                <div
                  key={p.label}
                  className="card-surface !p-5 hover:-translate-y-0.5 transition-transform duration-200"
                  style={{ boxShadow: "0 4px 16px rgba(11,30,58,0.06)" }}
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 bg-[#FFDA0B]">
                    <p.icon className="w-5 h-5 text-[#072A54]" strokeWidth={1.9} />
                  </div>
                  <div className="font-display text-[15px] font-semibold text-[#0B1E3A] tracking-tight">{p.label}</div>
                  <div className="text-[12.5px] text-[#3A5275] leading-relaxed mt-1.5">{p.body}</div>
                </div>
              ))}
            </div>

            {/* Feature strip (blue accents, not yellow — keeps yellow scarce & high-value) */}
            <div className="pt-4 grid grid-cols-2 md:grid-cols-4 gap-5">
              {[
                { icon: Mic,           title: "Voice dictation", body: "Hands-free with Whisper." },
                { icon: Camera,        title: "Photo → data",    body: "Med list + insurance card." },
                { icon: BookmarkCheck, title: "Saved profiles",  body: "Your agency, one tap." },
                { icon: FileStack,     title: "8-PDF packet",    body: "Pixel-perfect overlays." },
              ].map((f) => (
                <div key={f.title} className="space-y-2">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#E7F1FB] border border-[#C1D7EF]">
                    <f.icon className="w-4 h-4 text-[#0A3D79]" strokeWidth={1.8} />
                  </div>
                  <div className="font-display text-[13.5px] font-semibold text-[#0B1E3A]">{f.title}</div>
                  <div className="text-[12px] text-[#3A5275] leading-relaxed">{f.body}</div>
                </div>
              ))}
            </div>
          </div>

          {/* =========================  HERO · LOGO SHOWCASE ========================= */}
          <div className="lg:col-span-2">
            <div
              className="relative rounded-[28px] overflow-hidden border border-[#D6E3EF] flex items-center justify-center"
              style={{
                background: "linear-gradient(160deg, #FFFFFF 0%, #F0F6FC 45%, #E7F1FB 100%)",
                boxShadow: "0 24px 48px -16px rgba(11, 30, 58, 0.14), 0 8px 16px -6px rgba(11, 30, 58, 0.08)",
                minHeight: 620,
              }}
            >
              {/* Soft concentric rings */}
              <div
                className="absolute inset-0 pointer-events-none opacity-60"
                style={{
                  background:
                    "radial-gradient(circle at 50% 50%, rgba(31, 160, 230, 0.10) 0%, transparent 50%), " +
                    "radial-gradient(circle at 50% 50%, transparent 38%, rgba(10, 61, 121, 0.04) 40%, transparent 42%), " +
                    "radial-gradient(circle at 50% 50%, transparent 54%, rgba(10, 61, 121, 0.03) 56%, transparent 58%)",
                }}
                aria-hidden="true"
              />
              {/* Flat yellow "Tablet-first" chip */}
              <div className="absolute top-6 left-6 flex items-center gap-2 z-10">
                <span
                  className="px-3 h-7 rounded-full text-[11px] font-bold uppercase tracking-[0.12em] inline-flex items-center gap-1.5"
                  style={{ backgroundColor: "#FFDA0B", color: "#072A54" }}
                >
                  <Sparkles className="w-3 h-3" /> Tablet-first
                </span>
              </div>
              {/* Mini "by Avida" chip bottom-right */}
              <div className="absolute top-6 right-6 z-10">
                <span className="avida-accent-chip">est. 2026</span>
              </div>

              {/* Big centered logo */}
              <div className="relative z-10 flex flex-col items-center px-10 text-center">
                <AvidaLogo size="mega" />
                <div className="mt-6 h-px w-20 bg-[#DBB90A]" aria-hidden="true" />
                <div className="mt-5 text-[11px] uppercase tracking-[0.22em] font-bold text-[#0A3D79]">
                  Designed for the home visit
                </div>
                <div className="mt-3 font-display text-[22px] leading-tight tracking-tight text-[#0B1E3A] max-w-sm">
                  Large touch targets.<br />
                  Voice-first capture.<br />
                  <span className="text-[#0A3D79]">Signature-gated</span> submission.
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =========================  WORKFLOW STRIP ========================= */}
        <section className="mt-24 lg:mt-28">
          <div className="eyebrow mb-4">What this app builds — five stages</div>
          <h2 className="font-display text-2xl lg:text-3xl font-semibold text-[#0B1E3A] tracking-tight max-w-2xl">
            Bedside observation to a sealed, signature-ready bundle —{" "}
            <span className="yellow-underline">one</span> linear chain.
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-5 gap-4 stagger-in">
            {[
              { step: "01", title: "Capture",   body: "Steps 1–7. ADLs, NSA, meds, history, vitals, cert period." },
              { step: "02", title: "Verify",    body: "AI compliance check + 13-item RN checklist gate." },
              { step: "03", title: "Generate",  body: "Eight PDFs stamped from your findings, no retyping." },
              { step: "04", title: "Sign",      body: "Finger-drawn dual signatures embedded into every PDF." },
              { step: "05", title: "Hand off",  body: "Bundle dispatched via webhook for downstream review." },
            ].map((w, i, arr) => (
              <div key={w.step} className="relative">
                <div className="card-surface !p-5 h-full">
                  <div className="text-[11px] font-mono text-[#8A6B00] tracking-[0.16em] font-bold">{w.step}</div>
                  <div className="font-display text-[16px] font-semibold text-[#0B1E3A] mt-1 tracking-tight">{w.title}</div>
                  <div className="text-[12.5px] text-[#3A5275] leading-relaxed mt-1.5">{w.body}</div>
                </div>
                {i < arr.length - 1 && (
                  <ArrowRight
                    className="hidden md:block absolute top-1/2 -right-3 w-5 h-5 text-[#B7CADA] -translate-y-1/2"
                    strokeWidth={2}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="mt-6 text-[12px] text-[#3A5275] max-w-2xl">
            After hand-off, MD certification, payer approval, and care delivery are tracked
            and managed by the parent system — out of scope for this intake portal.
          </div>
        </section>

        {/* =========================  FOOTER ========================= */}
        <footer className="mt-24 pt-10 border-t border-[#D6E3EF] text-[12px] text-[#3A5275] flex flex-wrap items-center gap-x-6 gap-y-3">
          <span className="inline-flex items-center gap-2">
            <span className="avida-accent-chip">Avida Health</span>
            <span>Non-Skilled Assessment Workflow</span>
          </span>
          <span>Tablet-first · Voice-enabled · Signature-gated</span>
          <span>Zero PHI at rest until submission</span>
        </footer>
      </div>

      <CopyFromLastModal
        open={copyOpen}
        onClose={() => setCopyOpen(false)}
        onCopied={afterCopy}
      />
      <OrganizationPicker
        open={orgOpen}
        onClose={() => setOrgOpen(false)}
      />
    </main>
  );
}
